import axios from "axios";
import { getClientId } from "../services/Service";

const BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000').replace(/\/+$/, '');
const clientId = getClientId();
const setStoredValue = (key, value) => {
  const normalized = value ?? "";
  sessionStorage.setItem(key, normalized);
  localStorage.setItem(key, normalized);
};

const fetchData = async (method, url, data = {}) => {
  try {
    // get token from session storage (you store token on login)
    

    // const headers = {
    //   "Content-Type": "application/json",
    //   Accept: "application/json",
    //   // attach Authorization header if token exists
    //   ...(token ? { Authorization: `Bearer ${token}` } : {}),
    // };

    const getHeaders = () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      return {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
        Accept: "application/json",
      };
    };

    const response = await axios({
      method,
      url: `${BASE_URL}${url}`,
      data,
      // headers,
       headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    // DO NOT explicitly clear token here! 
    // It creates cross-component crashes if this specific API call throws a 
    // permission-denied 401 while other components are trying to load simultaneously.
    throw error;
  }
};

export const getInquiryStatusCount = async () => {
  const clientId = sessionStorage.getItem("clientId") || localStorage.getItem("clientId");
  try {
    const response = await axios.get(
      `${BASE_URL}/inquiry/status-count/${clientId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching inquiry status count:", error);
    throw error;
  }
};

export const getCallbackRequests = () =>
  fetchData("GET", "/tickets/all_callback_requests");

// *** UPDATED: store token from backend in sessionStorage ***
// verifyUserCredentials (updated)
export const verifyUserCredentials = async (credentials) => {
  if (!credentials.email || !credentials.password) {
    throw new Error("Email and password are required!");
  }

  const response = await fetchData("POST", "/verify-user-credentials", {
    email: credentials.email,
    password: credentials.password,
  });

  // DEBUG: log raw response to console so you can see actual shape
  console.log("API /verify-user-credentials response:", response);

  if (response.status === "success" && response.data) {
    // tolerant token extraction: support multiple possible field names
    const {
      user_id,
      client_id,
      role,
      username,
      user_name,
      abbreviation,
    } = response.data;

    // token might be in different keys depending on backend implementation
    const access_token =
      response.data.access_token ||
      response.data.token ||
      response.data.auth_token ||
      response.data.accessToken ||
      response.data?.data?.access_token; // extra fallback if nested

    // store token if provided by backend
    if (access_token) {
      setStoredValue("token", access_token);
    } else {
      // remove any stale token if backend didn't return one
      sessionStorage.removeItem("token");
      localStorage.removeItem("token");
      console.warn("No token returned from login response.");
    }

    // existing data stored
    setStoredValue("userId", user_id);
    setStoredValue("clientId", client_id); // note: unify key name (clientId vs client_id)
    setStoredValue("role", role);
    setStoredValue("email", username);
    setStoredValue("name", user_name);
    setStoredValue("abbreviation", abbreviation || "");
  }

  return response;
};

export const getInquiryByClientId = async (
  clientId,
  page = 1,
  page_size = 10
) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/inquiries/${clientId}?page=${page}&page_size=${page_size}`
      
    );

    if (response && response.data && response.data.status) {
      return response.data;
    } else {
      console.error("Invalid response data:", response);
      return { status: false, data: [] };
    }
  } catch (error) {
    console.error("Error fetching inquiries:", error);
    return { status: false, data: [] };
  }
};

export const updateInquiry = async ({
  p_id,
  user_id,
  p_status,
  p_agent_remarks,
  p_Next_followup,
}) => {
  try {
    const response = await axios.put(`${BASE_URL}/update-inquiry`, {
      p_id,
      user_id,
      p_status,
      p_agent_remarks,
      p_Next_followup,
    });

    return response.data;
  } catch (error) {
    console.error("Error updating inquiry:", error);
    throw error;
  }
};

export const getUserInquiry = async (userId, clientId) => {
  try {
    const trimmedUserId = userId.includes("-")
      ? userId.split("-").pop()
      : userId;

    const response = await axios.get(`${BASE_URL}/user-inquiry`, {
      params: {
        User_id: trimmedUserId,
        client_id: clientId,
      },
    });

    if (response.data?.status === "success") {
      const inquiry = response.data.data;
      return {
        success: true,
        id: inquiry.id,
        User_id: inquiry.User_id,
        user_id: inquiry.user_id,
        Client_name: inquiry.Client_name,
        contact: inquiry.contact,
        email: inquiry.email,
        created_at: inquiry.created_at,
        updated_at: inquiry.updated_at,
        Next_followup: inquiry.p_Next_followup,
        agent_remarks: inquiry.p_agent_remarks,
        status_code: inquiry.p_status,
        lastQuestion: {
          id: inquiry.last_question?.id || null,
          text: inquiry.last_question?.text || "",
        },
      };
    } else {
      return { success: false, data: null };
    }
  } catch (error) {
    console.error(
      `Error fetching user inquiry for user_id: ${userId} and client_id: ${clientId}`,
      error
    );
    return { success: false, data: null };
  }
};

export const getRecentInquiries = async (clientId) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/client/${clientId}/recent-inquiries`
    );

    if (response && response.data) {
      return {
        success: true,
        clientId: response.data.client_id,
        newInquiriesCount: response.data.new_inquiries_count,
        recentInquiries: response.data.recent_inquiries,
      };
    } else {
      console.error(
        "Unexpected response format from recent-inquiries API:",
        response
      );
      return { success: false, newInquiriesCount: 0, recentInquiries: [] };
    }
  } catch (error) {
    console.error(
      `Error fetching recent inquiries for client ${clientId}:`,
      error
    );
    return { success: false, newInquiriesCount: 0, recentInquiries: [] };
  }
};

export const getOpenInquiries = async (clientId, page = 1, page_size = 10) => {
  try {
    const response = await axios.get(`${BASE_URL}/inquiries/Open`, {
      params: {
        client_id: clientId,
        page,
        page_size: page_size,
      },
    });

    if (response && response.data && Array.isArray(response.data.data)) {
      return {
        success: true,
        totalCount: response.data.total_opn_count,
        dataCount: response.data.data_count,
        inquiries: response.data.data,
      };
    } else {
      console.error("Unexpected response structure:", response);
      return { success: false, inquiries: [] };
    }
  } catch (error) {
    console.error("Error fetching Open inquiries:", error);
    return { success: false, inquiries: [] };
  }
};

export const getInProgressInquiries = async (
  clientId,
  page = 1,
  page_size = 10
) => {
  try {
    const response = await axios.get(`${BASE_URL}/inquiries/In-Progress`, {
      params: {
        client_id: clientId,
        page,
        page_size: page_size,
      },
    });

    if (response && response.data && Array.isArray(response.data.data)) {
      return {
        success: true,
        totalCount: response.data.total_inp_count,
        dataCount: response.data.data_count,
        inquiries: response.data.data,
      };
    } else {
      console.error("Unexpected response structure:", response);
      return { success: false, inquiries: [] };
    }
  } catch (error) {
    console.error("Error fetching In-Progress inquiries:", error);
    return { success: false, inquiries: [] };
  }
};

export const getNoResponseInquiries = async (
  clientId,
  page = 1,
  page_size = 10
) => {
  try {
    const response = await axios.get(`${BASE_URL}/inquiries/No-Response`, {
      params: {
        client_id: clientId,
        page,
        page_size: page_size,
      },
    });

    if (response && response.data && Array.isArray(response.data.data)) {
      return {
        success: true,
        totalCount: response.data.total_cnr_count,
        dataCount: response.data.data_count,
        inquiries: response.data.data,
      };
    } else {
      console.error("Unexpected response structure (No-Response):", response);
      return { success: false, inquiries: [] };
    }
  } catch (error) {
    console.error("Error fetching No-Response inquiries:", error);
    return { success: false, inquiries: [] };
  }
};

export const getResolvedInquiries = async (
  clientId,
  page = 1,
  page_size = 10
) => {
  try {
    const response = await axios.get(`${BASE_URL}/inquiries/Resolved`, {
      params: {
        client_id: clientId,
        page,
        page_size: page_size,
      },
    });

    if (response && response.data && Array.isArray(response.data.data)) {
      return {
        success: true,
        totalCount: response.data.total_crs_count,
        dataCount: response.data.data_count,
        inquiries: response.data.data,
      };
    } else {
      console.error("Unexpected response structure (Resolved):", response);
      return { success: false, inquiries: [] };
    }
  } catch (error) {
    console.error("Error fetching Resolved inquiries:", error);
    return { success: false, inquiries: [] };
  }
};

export const fetchUserById = async (user_id, dynamicClientId = null) => {
  if (!user_id) {
    throw new Error("fetchUserById: user_id must be provided.");
  }

  const effectiveClientId = dynamicClientId || clientId;

  try {
    const response = await axios.get(`${BASE_URL}/manage-user`, {
      params: {
        p_ClientId: effectiveClientId,
        p_user_id: user_id,
        p_role_abbreviation: "S",
      },
      headers: { "Content-Type": "application/json" },
    });

    const { status, data } = response.data;

    if (status === "success" && Array.isArray(data) && data.length > 0) {
      return data[0];
    } else {
      console.warn("⚠️ No staff user found with ID:", user_id);
      return null;
    }
  } catch (err) {
    const errorMessage =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Unknown error while fetching staff user by ID";

    console.error("❌ fetchUserById error:", errorMessage);
    throw new Error(errorMessage);
  }
};

export const getInquiriesByDateRange = async (
  fromDate,
  toDate,
  clientId,
  page = 1,
  page_size = 10
) => {
  try {
    const response = await axios.get(`${BASE_URL}/get-inquiries-by-client`, {
      params: {
        from_date: fromDate,
        to_date: toDate,
        client_id: clientId,
        page,
        page_size,
      },
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching inquiries by date:", error);
    return { status: false, data: [], total: 0 };
  }
};

export const getNotificationCount = async (clientId) => {
  if (!clientId) {
    return { success: false, newInquiriesCount: 0, recentInquiries: [] };
  }
  try {
    const { success, newInquiriesCount, recentInquiries } =
      await getRecentInquiries(clientId);
    if (success) {
      return { success, newInquiriesCount, recentInquiries };
    } else {
      return { success: false, newInquiriesCount: 0, recentInquiries: [] };
    }
  } catch (error) {
    console.error("Failed to fetch notification count:", error);
    return { success: false, newInquiriesCount: 0, recentInquiries: [] };
  }
};

export const getTodaysFollowups = async (clientId) => {
  try {
    const response = await axios.get(`${BASE_URL}/followups/today/${clientId}`);

    if (response && response.data && Array.isArray(response.data)) {
      return {
        success: true,
        followups: response.data,
      };
    } else {
      console.error(
        "Unexpected response structure (Today's Followups):",
        response
      );
      return { success: false, followups: [] };
    }
  } catch (error) {
    console.error("Error fetching today's followups:", error);
    return { success: false, followups: [] };
  }
};

export const logoutUser = async (userId) => {
  if (!userId) {
    throw new Error("User ID is required to logout.");
  }

  const response = await fetchData("POST", "/logout", {
    user_id: userId,
  });

  return response;
};

export const getInquiryDuration = async (p_id) => {
  if (!p_id) {
    throw new Error("getInquiryDuration: 'p_id' is required.");
  }

  try {
    const response = await axios.get(`${BASE_URL}/get-inquiry-duration`, {
      params: { p_id },
    });
    console.log("🔍 API /get-inquiry-duration →", response.data);
    if (response && response.data) {
      return {
        success: true,
        inquiryId: response.data.inquiry_id,
        startedAt: response.data.started_at,
        endedAt: response.data.ended_at,
        duration: response.data.duration,
        status: response.data.status,
      };
    } else {
      console.error(
        "Unexpected response structure from get-inquiry-duration:",
        response
      );
      return { success: false, data: null };
    }
  } catch (error) {
    console.error("Error fetching inquiry duration:", error);
    return { success: false, data: null };
  }
};

export const getTicketsByClientId = async (clientId) => {
  if (!clientId) {
    throw new Error("Client ID is required.");
  }

  try {
    const response = await axios.get(
      `${BASE_URL}/tickets/details/${clientId}`
    );

    if (response && Array.isArray(response.data)) {
      return { success: true, tickets: response.data };
    } else {
      console.error("Unexpected response format:", response);
      return { success: false, tickets: [] };
    }
  } catch (error) {
    console.error("Error fetching tickets by client ID:", error);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
      tickets: [],
    };
  }
};

export const getTicketById = async (id) => {
  if (!id) {
    throw new Error("Ticket ID is required");
  }

  try {
    const response = await axios.get(`${BASE_URL}/get-ticket`, {
      params: { id },
      headers: { "Content-Type": "application/json" },
    });

    return response.data;
  } catch (err) {
    const message =
      err.response?.data?.message ||
      err.message ||
      "Error fetching ticket by ID";
    throw new Error(message);
  }
};

export const updateTicketActivity = async (payload) => {
  try {
    const response = await axios.put(
      `${BASE_URL}/update-ticket-activity`,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating ticket activity:", error);
    throw error;
  }
};

export const searchTicketActivity = async (assignedTo) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/search-ticket-activity`,
      {
        params: { assignedTo },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error searching ticket activity:", error);
    throw error;
  }
};

export const getTicketCountsByAgentId = async (agentId) => {
  if (!agentId) {
    throw new Error("Agent ID is required.");
  }

  try {
    const response = await axios.get(
      `${BASE_URL}/tickets/counts?agentId=${agentId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching ticket counts:", error);
    return {
      status: false,
      message: error.response?.data?.message || error.message,
      data: {},
    };
  }
};

export const getTodayAssignedComplaintNotifications = async () => {
  try {
    const response = await axios.get(
      `${BASE_URL}/notifications/today-assigned-complaints`
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching today's assigned complaint notifications:",
      error
    );
    return {
      status: false,
      admin_notification: [],
      visitor_notification: [],
      agent_notification: [],
    };
  }
};

export const getTicketByIdPublic = async (id) => {
  if (!id) throw new Error("Ticket ID is required.");

  try {
    const response = await axios.get(`${BASE_URL}/get-ticket`, { params: { id } });

    if (response.status === 200 && response.data?.status === true && response.data?.data) {
      const mainData = response.data.data;

      const complaintArray = Array.isArray(mainData.complaints)
        ? mainData.complaints
        : [];

      return {
        id: mainData.id,
        status: mainData.status,
        createdAt: mainData.createdAt,
        complaintname: mainData.complaintname,
        complaint_mobile: mainData.complaint_mobile,
        assignedTo: mainData.assignedTo,
        assignedName: mainData.assignedName,
        description: mainData.Description,
        complaints: complaintArray,
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error fetching public ticket:", error);
    return null;
  }
};

export const getTicketDetailsWithDevices = async (ticketId) => {
  if (!ticketId) {
    throw new Error("Ticket ID is required.");
  }

  try {
    const response = await axios.get(`${BASE_URL}/tickets/${ticketId}`);

    if (response && response.data) {
      const ticketData = response.data;

      const deviceIds = [];
      if (Array.isArray(ticketData.complaints)) {
        ticketData.complaints.forEach((complaint) => {
          if (complaint.device_id && complaint.device_id !== "null") {
            deviceIds.push(complaint.device_id);
          }
        });
      }

      const uniqueDeviceIds = [...new Set(deviceIds)];

      return {
        success: true,
        ticket: {
          ...ticketData,
          deviceIds: uniqueDeviceIds,
          deviceCount: uniqueDeviceIds.length,
        },
      };
    } else {
      return {
        success: false,
        message: "Invalid response format",
      };
    }
  } catch (error) {
    console.error("Error fetching ticket details:", error);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

export const getTicketsByAgent = async (clientId, agentId) => {
  if (!clientId || !agentId) {
    throw new Error("Both client ID and agent ID are required.");
  }

  try {
    const response = await axios.get(
      `${BASE_URL}/tickets/agent/${clientId}/${agentId}`
    );

    if (response && Array.isArray(response.data.tickets)) {
      return {
        success: true,
        tickets: response.data.tickets,
      };
    } else {
      console.error("Unexpected response format:", response);
      return {
        success: false,
        tickets: [],
      };
    }
  } catch (error) {
    console.error("Error fetching tickets by agent:", error);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
      tickets: [],
    };
  }
};

export const getTicketHistory = async (ticketId, clientId) => {
  try {
    const response = await axios.get(`${BASE_URL}/ticket-history`, {
      params: {
        ticketId,
        clientId,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching ticket history:", error);
    return {
      status: "error",
      message: error.response?.data?.message || "Unknown error occurred",
      data: [],
    };
  }
};

export const updateTicket = async (payload) => {
  try {
    const res = await axios.put(`${BASE_URL}/update-ticket`, payload);

    if (res.status === 200 && res.data?.status === true) {
      return res.data;
    } else {
      console.error("❌ Backend rejected ticket update:", res.data);
      throw new Error("Ticket update failed. Check the response.");
    }
  } catch (error) {
    console.error("❌ Error updating ticket:", error);
    throw new Error("Ticket update failed. Check the response.");
  }
};
